import { useMemo } from "react";
import { ChartDataItem, PieChartProps } from "../types/dashboard";
import { useUser } from "src/shared/context/UserContext";
import { getReceivedLabels, getNoDataMessage } from "../utils/dashboardLabels";

export const usePieChartData = ({
  Orders = 0,
  amount = 0,
  todaysOrderReceived = 0,
  outstandingLedgerReceived = 0,
  OutstandingAmount = 0,
  Discount = 0,
  DeliveryFee = 0,
  ParcelFee = 0,
  tips = 0,
  timeRange = "today",
  businessType = "",
  receivedLabels,
}: PieChartProps) => {
  const { userConfigration } = useUser();

  const toBoolean = (value: any) => value === true || value === "true";

  const parcelFeeEnabled = toBoolean(userConfigration?.parcelFeeEnabled);

  const tipsEnabled = toBoolean(userConfigration?.tipsEnabled);

  const showParcelFee = businessType === "Restaurant" && parcelFeeEnabled;

  const showTips = businessType === "Restaurant" && tipsEnabled;

  const defaultLabels = getReceivedLabels(timeRange as keyof typeof getReceivedLabels);

  const labels = receivedLabels || defaultLabels;

  // Total received for the current dashboard calculation
  const totalReceived = todaysOrderReceived + outstandingLedgerReceived;

  // Outstanding = Sales - Received
  const calculatedOutstanding = Math.max(0, amount - totalReceived);

  // Use supplied OutstandingAmount if available,
  // otherwise calculate it.
  const finalOutstanding = OutstandingAmount > 0 ? OutstandingAmount : calculatedOutstanding;

  const hasValidData = useMemo(() => {
    return true;
  }, []);

  const chartData: ChartDataItem[] = useMemo(() => {
    const data: ChartDataItem[] = [
      { name: "Orders", value: Orders, color: "#3b82f6" },
      { name: "Amount", value: amount, color: "#f59e0b" },
      { name: "Discount", value: Discount, color: "#EC4899" },
      { name: "Delivery Fee", value: DeliveryFee, color: "#8b5cf6" },
    ];

    if (showParcelFee) {
      data.push({ name: "Parcel Fee", value: ParcelFee, color: "#06b6d4" });
    }

    if (showTips) {
      data.push({ name: "Tips", value: tips, color: "#f472b6" });
    }

    // Received split
    data.push(
      { name: labels.order, value: todaysOrderReceived, color: "#10b981" },
      { name: labels.ledger, value: outstandingLedgerReceived, color: "#34d399" },
    );

    // IMPORTANT:
    // Always add Outstanding, including when value = 0.
    data.push({ name: "Outstanding", value: finalOutstanding, color: "#ef4444" });

    return data;
  }, [Orders, amount, todaysOrderReceived, outstandingLedgerReceived, finalOutstanding, Discount, DeliveryFee, ParcelFee, tips, showParcelFee, showTips, labels]);

  const noDataMessage = getNoDataMessage(timeRange as keyof typeof getNoDataMessage);

  return {
    hasValidData,
    chartData,
    noDataMessage,
    totalReceived,
    calculatedOutstanding,
    receivedLabels: labels,
  };
};
